import React, { useState, useEffect } from 'react';
import { 
  CheckCircle2, Clock, QrCode, Copy, ExternalLink, ShieldCheck, 
  ArrowRight, Phone, MessageCircle, AlertCircle, FileText, Check,
  CreditCard, Sparkles, User, Calendar, DollarSign
} from 'lucide-react';
import { CustomerInvoice, ClientEntity, ClientPaymentConfig } from '../types';
import { db } from '../services/db';
import { toast } from 'sonner';
import { extractBrandingFromHtml } from '../utils/brandExtractor';

interface PublicInvoiceViewProps {
  invoiceId: string;
}

export const PublicInvoiceView: React.FC<PublicInvoiceViewProps> = ({ invoiceId }) => {
  const [invoice, setInvoice] = useState<CustomerInvoice | null>(null);
  const [client, setClient] = useState<ClientEntity | null>(null);
  const [payConfig, setPayConfig] = useState<ClientPaymentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [pixCopied, setPixCopied] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<'pix' | 'card'>('pix');
  const [markingPaid, setMarkingPaid] = useState(false);

  useEffect(() => {
    const loadInvoice = async () => {
      setLoading(true);
      try {
        const inv = await db.getInvoiceById(invoiceId);
        if (inv) {
          setInvoice(inv);
          // Load merchant
          /*
           * Aqui havia o mesmo login automatico com credencial fixa que estava
           * em `CustomerPortal.tsx` — usuario e senha de um cliente real,
           * versionados e no bundle. Uma fatura cujo emissor nao resolve nao
           * vira "mostre os dados de outro cliente": ela mostra a fatura sem os
           * dados do emissor, que e o que se sabe.
           */
          const clientData = await db.getClientBySlug(inv.clientSlug || inv.clientId);
          if (clientData) {
            setClient(clientData);
            const pConfig = await db.getClientPaymentConfig(clientData.id);
            setPayConfig(pConfig);
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    loadInvoice();
  }, [invoiceId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white max-w-md w-full rounded-3xl p-8 text-center shadow-2xl">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} />
          </div>
          <h2 className="text-xl font-bold text-slate-900">Fatura Não Encontrada</h2>
          <p className="text-slate-500 text-sm mt-2">
            Verifique o link informado ou entre em contato com o responsável pelo atendimento.
          </p>
        </div>
      </div>
    );
  }

  const brand = client ? extractBrandingFromHtml(client.htmlContent, client.data) : {
    primaryColor: '#059669',
    secondaryColor: '#10b981',
    businessName: 'Central de Atendimento'
  };

  const isPaid = invoice.status === 'paid';
  const companyName = client?.data?.name || 'Thomas Gabriel Consultoria';
  const rawPhone = client?.data?.phoneNumber?.replace(/[^0-9]/g, '') || '5511999999999';
  const waContactUrl = `https://wa.me/55${rawPhone}?text=Ol%C3%A1!%20Realizei%20o%20pagamento%20da%20fatura%20${invoice.invoiceNumber}%20no%20valor%20de%20R$%20${invoice.amount.toFixed(2)}`;

  const handleCopyPix = () => {
    if (!invoice.pixCode) return;
    navigator.clipboard.writeText(invoice.pixCode);
    setPixCopied(true);
    toast.success('Código Pix Copia e Cola copiado com sucesso!');
    setTimeout(() => setPixCopied(false), 3000);
  };

  const handleConfirmPixPayment = async () => {
    setMarkingPaid(true);
    try {
      await db.payCustomerInvoice(invoice.id, 'pix');
      setInvoice(prev => prev ? { ...prev, status: 'paid', paidAt: new Date().toISOString() } : null);
      toast.success('Pagamento confirmado com sucesso!');
    } catch (e) {
      toast.error('Erro ao confirmar pagamento.');
    } finally {
      setMarkingPaid(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-between py-8 px-4 font-sans selection:bg-emerald-500 selection:text-white">
      <div className="max-w-xl w-full mx-auto space-y-6">
        
        {/* Merchant Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold uppercase tracking-wider backdrop-blur-sm border border-white/10">
            <ShieldCheck size={14} className="text-emerald-400" />
            Ambiente Seguro de Pagamento
          </div>
          <h1 className="text-2xl font-black tracking-tight text-white">{companyName}</h1>
          <p className="text-slate-400 text-xs">Fatura Oficial de Serviços • {invoice.invoiceNumber}</p>
        </div>

        {/* Main Invoice Card */}
        <div className="bg-white text-slate-900 rounded-3xl shadow-2xl overflow-hidden border border-slate-100">
          
          {/* Header Status Bar */}
          <div className={`p-6 text-center ${isPaid ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-white'}`}>
            <div className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest mb-1 opacity-90">
              {isPaid ? (
                <>
                  <CheckCircle2 size={16} /> Fatura Paga & Confirmada
                </>
              ) : (
                <>
                  <Clock size={16} className="text-amber-400" /> Aguardando Pagamento
                </>
              )}
            </div>

            <div className="text-4xl font-black tracking-tight mt-2">
              R$ {invoice.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>

            <p className="text-xs opacity-75 mt-1">
              {isPaid ? `Quitado em ${new Date(invoice.paidAt || Date.now()).toLocaleDateString('pt-BR')}` : `Vencimento em ${invoice.dueDate}`}
            </p>
          </div>

          {/* Customer & Service Details */}
          <div className="p-6 border-b border-slate-100 space-y-3">
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-400 font-medium">Cliente</span>
              <span className="font-bold text-slate-900">{invoice.customerName}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-400 font-medium">Serviço / Referência</span>
              <span className="font-bold text-slate-900">{invoice.title}</span>
            </div>
            {invoice.description && (
              <p className="text-xs text-slate-500 bg-slate-50 p-3 rounded-xl border border-slate-100">
                {invoice.description}
              </p>
            )}
          </div>

          {/* Payment Options (If Not Paid) */}
          {!isPaid ? (
            <div className="p-6 space-y-6">
              
              {/* Method Switcher */}
              <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-2xl">
                <button
                  type="button"
                  onClick={() => setSelectedMethod('pix')}
                  className={`py-2.5 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                    selectedMethod === 'pix' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <QrCode size={16} className="text-emerald-600" /> Pix Instantâneo
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedMethod('card')}
                  className={`py-2.5 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                    selectedMethod === 'card' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <CreditCard size={16} className="text-blue-600" /> Cartão (Até 12x)
                </button>
              </div>

              {/* TAB 1: PIX */}
              {selectedMethod === 'pix' && (
                <div className="space-y-4 text-center">
                  
                  {/* QR Code Container */}
                  <div className="w-52 h-52 mx-auto bg-slate-50 rounded-2xl border-2 border-dashed border-emerald-500/30 flex items-center justify-center p-3 shadow-inner">
                    <img 
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(invoice.pixCode || 'PIX')}`}
                      alt="QR Code Pix"
                      className="w-full h-full object-contain rounded-xl"
                    />
                  </div>

                  <p className="text-xs text-slate-500 font-medium">
                    Abra o app do seu banco, escolha <b>Pagar com Pix</b> e escaneie o código acima ou copie a chave abaixo:
                  </p>

                  {/* Copy Paste Code Button */}
                  <button
                    type="button"
                    onClick={handleCopyPix}
                    className="w-full py-3.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow-lg shadow-emerald-600/20 transition-all flex items-center justify-center gap-2"
                  >
                    {pixCopied ? <Check size={18} /> : <Copy size={18} />}
                    {pixCopied ? 'Chave Pix Copiada com Sucesso!' : 'Copiar Código Pix Copia e Cola'}
                  </button>

                  <button
                    type="button"
                    onClick={handleConfirmPixPayment}
                    disabled={markingPaid}
                    className="w-full py-2.5 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition-all flex items-center justify-center gap-2"
                  >
                    <CheckCircle2 size={14} className="text-emerald-600" />
                    {markingPaid ? 'Processando...' : 'Já Realizei o Pagamento'}
                  </button>
                </div>
              )}

              {/* TAB 2: CARTÃO DE CRÉDITO */}
              {selectedMethod === 'card' && (
                <div className="space-y-4 text-center">
                  <div className="p-5 rounded-2xl bg-blue-50/60 border border-blue-100 text-left space-y-3">
                    <div className="flex items-center gap-2 text-blue-900 font-bold text-sm">
                      <CreditCard size={18} /> Pagamento Parcelado no Cartão
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      {payConfig?.cardLinkInstructions || 'Você será redirecionado para o checkout oficial seguro para parcelar em até 12x no cartão de crédito.'}
                    </p>
                  </div>

                  {invoice.cardPaymentUrl || payConfig?.cardPaymentLink ? (
                    <a
                      href={invoice.cardPaymentUrl || payConfig?.cardPaymentLink}
                      target="_blank"
                      rel="noreferrer"
                      className="w-full py-3.5 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm shadow-lg shadow-blue-600/20 transition-all flex items-center justify-center gap-2 inline-flex"
                    >
                      Ir para Checkout no Cartão <ExternalLink size={16} />
                    </a>
                  ) : (
                    <a
                      href={waContactUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="w-full py-3.5 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all flex items-center justify-center gap-2 inline-flex"
                    >
                      <MessageCircle size={16} /> Solicitar Link de Cartão no WhatsApp
                    </a>
                  )}
                </div>
              )}

            </div>
          ) : (
            /* PAID RECEIPT VIEW */
            <div className="p-8 text-center space-y-4">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
                <CheckCircle2 size={36} />
              </div>
              <h3 className="text-xl font-black text-slate-900">Comprovante de Pagamento</h3>
              <p className="text-xs text-slate-500 max-w-sm mx-auto">
                Seu pagamento foi confirmado pelo sistema da empresa. O comprovante foi arquivado em sua área de cliente.
              </p>

              <div className="pt-2 flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={`/#/atendimento/${client?.slug || 'thomas'}`}
                  className="px-6 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold shadow-md transition-all flex items-center justify-center gap-2"
                >
                  <User size={14} /> Acessar Minha Área do Cliente
                </a>

                <a
                  href={waContactUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-6 py-3 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold border border-emerald-200 transition-all flex items-center justify-center gap-2"
                >
                  <MessageCircle size={14} /> Avisar no WhatsApp
                </a>
              </div>
            </div>
          )}

          {/* Footer Security */}
          <div className="p-4 bg-slate-50 border-t border-slate-100 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
            <ShieldCheck size={14} className="text-emerald-600" />
            Processado via Infraestrutura Criptografada 256-bit
          </div>
        </div>

        {/* Back Link */}
        <div className="text-center">
          <a
            href={`/#/atendimento/${client?.slug || 'thomas'}`}
            className="text-xs font-bold text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1.5"
          >
            Acessar Área do Cliente de {companyName} <ArrowRight size={14} />
          </a>
        </div>

      </div>
    </div>
  );
};
